import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'
import { Check, Crown, Zap, Loader2, Sparkles } from 'lucide-react'
import { SubscriptionTypeEnum } from '../../lib/enums'
import { cn } from '../../lib/utils'
import { useLemonSqueezy } from '../../hooks/use-lemon-squeezy'
import { useAuth } from '../../hooks/use-firebase'
import { toast } from 'sonner'
import initializeLemonSqueezy from '../../lemonSqueezy'
import TermsOfServiceDialog from '../TermsOfServiceDialog'
import PrivacyPolicyDialog from '../PrivacyPolicyDialog'

interface SubscriptionOnboardingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PlanFeature {
  text: string
}

interface PlanDetails {
  name: string
  tier: SubscriptionTypeEnum
  price: string
  priceMonthly: number
  description: string
  icon: React.ReactNode
  color: string
  features: PlanFeature[]
  highlight?: boolean
  cta: string
  comingSoon?: boolean
}

const plans: PlanDetails[] = [
  {
    name: 'Basic',
    tier: SubscriptionTypeEnum.Basic,
    price: '$9.99',
    priceMonthly: 9.99,
    description: 'Perfect for getting started',
    icon: <Zap className="w-6 h-6" />,
    color: 'border-neutral-300 dark:border-neutral-700',
    cta: 'Subscribe to Basic',
    features: [
      { text: 'Up to 200 inventory items' },
      { text: 'Inventory tracking' },
      { text: 'Listing management' },
      { text: 'Sales tracking' },
      { text: 'Earnings dashboard' },
      { text: 'Community support' },
    ],
  },
  {
    name: 'Premium',
    tier: SubscriptionTypeEnum.Premium,
    price: '$19.99',
    priceMonthly: 19.99,
    description: 'Best for growing businesses',
    icon: <Crown className="w-6 h-6" />,
    color: 'border-sumoPrimary',
    highlight: true,
    cta: 'Subscribe to Premium',
    features: [
      { text: 'Up to 1000 inventory items' },
      { text: 'Barcode generation & printing' },
      { text: 'Export inventory CSV' },
      { text: 'Advanced reporting' },
      { text: 'Priority support' },
      { text: 'Enhanced analytics' },
    ],
  },
  {
    name: 'Enterprise',
    tier: SubscriptionTypeEnum.Enterprise,
    price: '$49.99',
    priceMonthly: 49.99,
    description: 'For large-scale operations',
    icon: <Sparkles className="w-6 h-6" />,
    color: 'border-sumoAmber',
    cta: 'Subscribe to Enterprise',
    comingSoon: true,
    features: [
      { text: 'Unlimited inventory items' },
      { text: 'Multi-user access' },
      { text: 'API access' },
      { text: 'Custom integrations' },
      { text: 'Dedicated support' },
      { text: 'Custom training' },
    ],
  },
]

const SubscriptionOnboardingDialog: FC<SubscriptionOnboardingDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { createCheckoutSession } = useLemonSqueezy()
  const auth = useAuth()
  const [loadingPlan, setLoadingPlan] = useState<SubscriptionTypeEnum | null>(null)

  const handleSelectPlan = async (tier: SubscriptionTypeEnum) => {
    if (!auth.currentUser?.email) {
      toast.error('User not authenticated')
      return
    }

    const storeId = process.env.REACT_APP_LEMON_SQUEEZY_STORE_ID
    const variantId = tier === SubscriptionTypeEnum.Basic
      ? process.env.REACT_APP_LEMON_SQUEEZY_BASIC_VARIANT_ID
      : tier === SubscriptionTypeEnum.Premium
      ? process.env.REACT_APP_LEMON_SQUEEZY_PREMIUM_VARIANT_ID
      : process.env.REACT_APP_LEMON_SQUEEZY_ENTERPRISE_VARIANT_ID

    if (!storeId || !variantId) {
      toast.error('Subscription system not configured. Please contact support.')
      return
    }

    setLoadingPlan(tier)
    try {
      initializeLemonSqueezy()

      const checkout = await createCheckoutSession(storeId, variantId, {
        checkoutData: {
          email: auth.currentUser.email,
          custom: {
            firebase_uid: auth.currentUser.uid
          }
        }
      })

      if (checkout?.checkoutUrl) {
        toast.success('Redirecting to checkout...')
        window.location.href = checkout.checkoutUrl
      } else {
        toast.error('Failed to create checkout session')
        setLoadingPlan(null)
      }
    } catch (error) {
      console.error('Checkout error:', error)
      toast.error('Failed to start checkout. Please try again.')
      setLoadingPlan(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">
            Welcome to InventorySumo! 🎉
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Choose a subscription plan to start managing your inventory
          </DialogDescription>
        </DialogHeader>

        {/* Info Banner */}
        <div className="p-4 bg-sumoPrimary/10 dark:bg-sumoPrimary/20 rounded-lg border border-sumoPrimary/30 mb-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-sumoPrimary/20">
              <Zap className="w-5 h-5 text-sumoPrimary" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                Subscription Required
              </h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                To use InventorySumo, please select a subscription plan below.
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {plans.map((plan) => {
            const isLoading = loadingPlan === plan.tier

            return (
              <Card
                key={plan.tier}
                className={cn(
                  'p-6 relative',
                  plan.highlight && 'border-2',
                  plan.color,
                  plan.comingSoon && 'opacity-60'
                )}
              >
                {/* Most Popular Badge */}
                {plan.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy">
                    Most Popular
                  </Badge>
                )}

                {/* Coming Soon Badge */}
                {plan.comingSoon && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neutral-500 text-white">
                    Coming Soon
                  </Badge>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div className="p-3 rounded-full bg-sumoPrimary/10 text-sumoPrimary">
                      {plan.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    {plan.description}
                  </p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-neutral-900 dark:text-neutral-100">
                      {plan.price}
                    </span>
                    <span className="text-neutral-600 dark:text-neutral-400">/month</span>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-6 min-h-[180px]">
                  {plan.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-sm"
                    >
                      <div className="mt-0.5 rounded-full p-0.5 bg-success500/10">
                        <Check className="w-3 h-3 text-success500" />
                      </div>
                      <span className="text-neutral-700 dark:text-neutral-300">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <Button
                  onClick={() => handleSelectPlan(plan.tier)}
                  disabled={isLoading || plan.comingSoon}
                  variant={plan.highlight && !plan.comingSoon ? 'primary' : 'outline'}
                  className="w-full"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : plan.comingSoon ? (
                    'Coming Soon'
                  ) : (
                    <>
                      {plan.icon}
                      <span className="ml-2">{plan.cta}</span>
                    </>
                  )}
                </Button>
              </Card>
            )
          })}
        </div>

        {/* Footer Notes */}
        <div className="mt-4 space-y-3">
          <div className="p-3 bg-sumoPrimary/5 rounded-lg border border-sumoPrimary/20">
            <p className="text-xs text-neutral-600 dark:text-neutral-400 text-center">
              Cancel anytime • Manage your subscription from Settings
            </p>
          </div>

          {/* Legal Notice */}
          <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <p className="text-xs text-neutral-600 dark:text-neutral-400 text-center">
              By subscribing, you agree to our{' '}
              <span className="inline-block" onClick={(e) => e.stopPropagation()}>
                <TermsOfServiceDialog
                  variant="link"
                  triggerClassName="text-sumoPrimary hover:text-sumoOrange underline underline-offset-2 text-xs"
                  linkText="Terms of Service"
                />
              </span>
              {' '}and{' '}
              <span className="inline-block" onClick={(e) => e.stopPropagation()}>
                <PrivacyPolicyDialog
                  variant="link"
                  triggerClassName="text-sumoPrimary hover:text-sumoOrange underline underline-offset-2 text-xs"
                  linkText="Privacy Policy"
                />
              </span>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SubscriptionOnboardingDialog
